import { type VariantProps, cva } from "class-variance-authority";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

export const badgeVariants = cva(
  "inline-flex items-center gap-x-1.5 rounded-full font-medium justify-center",
  {
    variants: {
      size: {
        sm: "px-2 py-0.5 text-xs",
        md: "px-2.5 py-1 text-sm",
      },
      color: {
        primary: "bg-primary-50 text-primary-700",
        error: "bg-error-50 text-error-700",
        success: "bg-success-50 text-success-700",
        warning: "bg-warning-50 text-warning-700",
        grey: "bg-grey-100 text-grey-700",
        dark: "bg-grey-900 mix-blend-normal text-grey-300",
      },
      variant: {
        soft: "mix-blend-multiply",
        solid: undefined,
      },
    },
    defaultVariants: {
      size: "sm",
      color: "primary",
      variant: "soft",
    },
    compoundVariants: [
      {
        variant: "soft",
        color: "primary",
        className: "bg-primary-50 text-primary-700",
      },
      {
        variant: "soft",
        color: "error",
        className: "bg-error-50 text-error-700",
      },
      {
        variant: "soft",
        color: "success",
        className: "bg-success-50 text-success-700",
      },
      {
        variant: "soft",
        color: "warning",
        className: "bg-warning-50 text-warning-700",
      },
      {
        variant: "soft",
        color: "grey",
        className: "bg-grey-100 text-grey-700",
      },
      {
        variant: "soft",
        color: "dark",
        className: "bg-grey-900 text-grey-300",
      },
      {
        variant: "solid",
        color: "primary",
        className: "bg-primary-400 text-white",
      },
    ],
  },
);

type BadgeProps = {
  testId?: string;
  type?: "dot" | "text" | "icon";
  children: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof badgeVariants>;

const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      testId,
      color,
      size,
      type = "text",
      children,
      className,
      variant,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        {...props}
        ref={ref}
        className={twMerge(badgeVariants({ size, color, variant }), className)}
        data-cy={testId}
      >
        {type === "dot" && (
          <div
            className={twMerge(
              cva("bg-grey-400 m-px h-1.5 w-1.5 rounded-lg", {
                variants: {
                  color: {
                    primary: "bg-primary-400",
                    error: "bg-error-400",
                    success: "bg-success-400",
                    warning: "bg-warning-400",
                    grey: "bg-grey-400",
                    dark: "bg-grey-300",
                  },
                },
                defaultVariants: { color: "primary" },
              })({ color }),
            )}
          />
        )}
        <span className="flex items-center gap-3 truncate">{children}</span>
      </div>
    );
  },
);

Badge.displayName = "Badge";

export { Badge, type BadgeProps };
